import DeviceConfig from '@devices/config';
import { IKeyboard } from '../../../types/interfaces/keyboard';
import { Keys } from '@devices/keys';
import { store } from '../../../store/store';
import { useEffect } from 'react';

const PRESSED_KEYS = 20;

function useKeyboard({
  focus = undefined,
  canBeFocused = true,
  stopPropagation = false,
  keyDownHandler,
  keyUpHandler,
  keyPressHandler,
  pointerUpHandler,
}: IKeyboard) {
  useEffect(() => {
    let lastKey = '';
    let pressed = 0;
    let lastFocus = -1;
    let keyTimeout: ReturnType<typeof setTimeout> | undefined = undefined;
    const isFocused = () => {
      const { focus: state } = store.getState();
      if (state.focus === null) return false;
      return (
        state.focus === focus ||
        ((state.focus as number[])?.length &&
          (state.focus as number[]).includes(focus as number))
      );
    };
    const keyDown = (event: KeyboardEvent) => {
      if (keyDownHandler) {
        if (isFocused() || !canBeFocused) {
          const parsedKey =
            Keys[event.key] || Keys[event.code] || Keys[event.keyCode];
          if (lastKey === parsedKey) {
            pressed += 1;
          } else {
            pressed = 0;
          }
          lastKey = parsedKey;
          keyDownHandler(parsedKey, pressed > PRESSED_KEYS, pressed);
          clearTimeout(keyTimeout);
          if (DeviceConfig.capabilities.repeatSelect) {
            lastFocus = focus || -1;
          }
          if (parsedKey === 'SELECT' && keyPressHandler) {
            if (!DeviceConfig.capabilities.repeatSelect) {
              keyTimeout = setTimeout(() => {
                keyPressHandler();
              }, 2000);
            } else if (pressed > PRESSED_KEYS) {
              keyPressHandler();
            }
          }
          if (stopPropagation) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        }
      }
    };
    const keyUp = (event: KeyboardEvent) => {
      clearTimeout(keyTimeout);
      if (keyUpHandler && (isFocused() || !canBeFocused)) {
        const pressedKey =
          (event.key !== 'Unidentified' ? event.key : null) ||
          event.code ||
          event.keyCode;
        const parsedkey = Keys[pressedKey];
        if (
          lastKey === parsedkey ||
          (parsedkey === 'SELECT' &&
            !DeviceConfig.capabilities.repeatSelect &&
            lastFocus === focus)
        ) {
          keyUpHandler(parsedkey, pressed > PRESSED_KEYS, pressed);
        }
        if (stopPropagation) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }
      pressed = 0;
    };
    const pointerUp = () => {
      const { focus: state } = store.getState();
      if (pointerUpHandler && (state.focus === focus || !canBeFocused)) {
        pointerUpHandler();
      }
      pressed = 0;
    };
    const wheel = (event: Event) => {
      if (isFocused() || !canBeFocused) {
        let parsedKey = '';
        const wheelEvent = event as WheelEvent;
        if ((wheelEvent?.deltaY || 0) > 0) {
          parsedKey = 'DOWN';
        } else {
          parsedKey = 'UP';
        }
        if (lastKey === parsedKey) {
          pressed += 1;
        } else {
          pressed = 0;
        }
        lastKey = parsedKey;
        keyDownHandler &&
          keyDownHandler(parsedKey, pressed > PRESSED_KEYS, pressed);
        if (stopPropagation) {
          event.stopImmediatePropagation();
        }
      }
    };
    document.addEventListener('keydown', keyDown);
    document.addEventListener('keyup', keyUp);
    document.addEventListener('pointerup', pointerUp);
    document.addEventListener('mousewheel', wheel);
    return () => {
      clearTimeout(keyTimeout);
      document.removeEventListener('keydown', keyDown);
      document.removeEventListener('keyup', keyUp);
      document.removeEventListener('mousewheel', wheel);
      document.removeEventListener('pointerup', pointerUp);
    };
  }, []);
}

export default useKeyboard;
